<template>
  <!--
    Kundenfeedback 18.10.2026 (XII/XIII) – Dialog „Beteiligte zuordnen“:
    links die Rollenliste (Ablageziel für Drag&Drop), rechts die Suche mit
    drei Reitern „Suche gesamt“ | „Favoriten“ | „Eigene Kontakte“. Jede
    Adresse kann in jeder Rolle zugeordnet werden; kein Treffer → „Neue
    Adresse anlegen“.
    Kundenfeedback 24.10.2026 (XVIII–XX): ganzes Fenster weiß, links oben eine
    Suche, darunter nur die Rollen (besetzt = grün). Klick auf eine besetzte
    Rolle zeigt rechts deren Beteiligte (RoleDetailPanel), Klick auf eine
    unbesetzte Rolle die Suche für diese Rolle. Bei ausgeschalteten
    Benachrichtigungen lassen sie sich direkt hier ändern.
    Props, Events und openDialog(role, { query }) sind für alle Aufrufer
    unverändert.
  -->
  <v-dialog v-model="visible" v-bind="win.dialogProps" max-width="1120" @click:outside.prevent>
    <div class="apd" :ref="win.setCardEl" data-testid="apd">
      <div class="apd__head dialog-window-handle" @pointerdown="win.onHandlePointerDown" @dblclick="win.onHandleDblClick">
        <div>
          <h3 class="apd__title">{{ $gt('Beteiligte zuordnen') }}</h3>
          <p v-if="caseTitle" class="apd__sub">{{ caseTitle }}</p>
        </div>
        <button type="button" class="apd__close" :title="$gt('Schließen')" :aria-label="$gt('Schließen')" data-testid="apd-close" @click="closeDialog">
          <X :size="18" aria-hidden="true" />
        </button>
      </div>

      <div v-if="loading" class="apd__loading"><v-progress-circular indeterminate color="primary" size="32" /></div>

      <div v-else class="apd__split">
        <aside class="apd__left">
          <RoleColumn
            :groups="roleGroups"
            :active-role="targetRole"
            :drag-active="!!draggedHit"
            :query="searchQuery"
            @select-role="onSelectRole"
            @drop-role="onDropRole"
            @search="onLeftSearch"
          />
        </aside>

        <div class="apd__right">
          <div v-if="caseNotificationsDisabled" class="apd__note" data-testid="apd-notifications-off">
            <BellOff :size="15" class="apd__note-ico" aria-hidden="true" />
            <span class="apd__note-text">{{ $gt('Für diesen Fall sind alle E-Mail-Benachrichtigungen ausgeschaltet.') }}</span>
            <button
              v-if="canEnableNotifications"
              type="button"
              class="apd__note-btn"
              :disabled="notifyBusy"
              :title="$gt('Benachrichtigungs-E-Mails für diesen Fall wieder einschalten')"
              data-testid="apd-notifications-enable"
              @click="enableCaseNotifications"
            >{{ $gt('Einschalten') }}</button>
            <button
              v-if="canOpenNotificationSettings"
              type="button"
              class="apd__note-btn"
              :title="$gt('Benachrichtigungen für den ganzen Fall und für einzelne Beteiligte einstellen')"
              data-testid="apd-notifications-settings"
              @click="openNotificationSettings"
            >{{ $gt('Einstellungen ändern') }}</button>
          </div>
          <div v-if="hasMultipleVehicles" class="apd__vehicle">
            <span class="apd__vehicle-label">{{ $gt('Zuordnen zu Fahrzeug') }}</span>
            <v-select v-model="targetVehicleId" :items="vehicleOptions" item-title="title" item-value="value" density="compact" variant="outlined" hide-details class="apd__vehicle-select" />
          </div>
          <AfterAssignCard
            v-if="lastAssigned"
            :entry="lastAssigned"
            :busy="isFollowUpBusy"
            @invite="inviteLastAssigned"
            @adopt="adoptLastAssigned"
            @dismiss="lastAssigned = null"
          />
          <RoleDetailPanel
            v-if="mode === 'search' && activeRoleGroup"
            :group="activeRoleGroup"
            :busy="isFollowUpBusy"
            @add="onAddToRole"
            @remove="$emit('remove', $event)"
            @invite="inviteMember"
          />
          <NewAddressForm
            v-if="mode === 'new'"
            :key="newFormKey"
            :initial-name="newPrefill.name"
            :initial-role="newPrefill.role"
            :role-choices="roleChoiceList"
            :busy="isCreating"
            :known-contacts="contacts"
            @submit="createNewAddress"
            @use-contact="assignExistingContact"
            @cancel="mode = 'search'"
          />
          <ContactSearchPanel
            v-show="mode === 'search' && !activeRoleGroup"
            ref="panel"
            :contacts="contacts"
            :participants="activeParticipants"
            :suggestions="caseSuggestions"
            :target-role="targetRole"
            :case-muted="caseNotificationsDisabled"
            :busy-key="busyKey"
            @assign="assign"
            @toggle-favorite="toggleFavorite"
            @drag-start="draggedHit = $event"
            @drag-end="draggedHit = null"
            @create-new="openNewAddress"
            @open-contacts="openContacts"
            @query-change="searchQuery = $event"
            @clear-role="targetRole = ''"
          />
        </div>
      </div>

      <div class="apd__foot">
        <span class="apd__foot-hint">{{ $gt('Zugeordnete Beteiligte erscheinen sofort links bei ihrer Rolle.') }}</span>
        <button type="button" class="apd__btn-close" @click="closeDialog">{{ $gt('Fertig') }}</button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { BellOff, UserPlus, X } from "lucide-vue-next";
import { ref } from "vue";
import { useDialogWindow } from "@/composables/useDialogWindow";
import { getRequestGeneric } from "@/services/backendApiGeneric";
import { createContact, emptyContact, getContacts } from "@/services/contactsApi";
import { loadCaseNotificationSettings } from "@/services/caseNotificationsApi";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { contactToHit } from "@/utils/contactSearchHits";
import AfterAssignCard from "./beteiligte/dialog/AfterAssignCard.vue";
import ContactSearchPanel from "./beteiligte/dialog/ContactSearchPanel.vue";
import NewAddressForm from "./beteiligte/dialog/NewAddressForm.vue";
import RoleColumn from "./beteiligte/dialog/RoleColumn.vue";
import RoleDetailPanel from "./beteiligte/dialog/RoleDetailPanel.vue";
import { adoptHitAsContact, canAdoptHit, toggleContactFavorite } from "./beteiligte/dialog/contactAdopt";
import { assignHit, inviteExternal } from "./beteiligte/dialog/participantAssign";
import {
  MANDANT_ROLE_KEY,
  buildCaseSuggestions,
  buildRoleColumn,
  normalizeRole,
  roleChoices,
  roleLabel,
} from "./beteiligte/dialog/participantDialogModel";

function snack(message, type = "success") {
  useSnackbarStore().showSnackbar({ message, type });
}

export default {
  name: "AddParticipantDialog",

  components: { AfterAssignCard, BellOff, ContactSearchPanel, NewAddressForm, RoleColumn, RoleDetailPanel, X },

  // Fall-Seite (Page.vue): Benachrichtigungs-Einstellungen der Akte öffnen.
  // Außerhalb der Fall-Seite fehlt sie – dann nur der Hinweis ohne Knöpfe.
  inject: { caseDetailPage: { default: null } },

  setup() {
    const visible = ref(false);
    const win = useDialogWindow({ id: "add-participant", title: "Beteiligte zuordnen", icon: UserPlus, isOpen: visible });
    return { visible, win };
  },

  props: {
    caseId: { type: Number, required: true },
    caseTitle: { type: String, required: true },
    existingParticipants: { type: Array, default: () => [] },
    autoInsurances: { type: Array, default: () => [] },
    // Issue #352: Fahrzeuge des Falls + aktives Fahrzeug (Zuordnung je Fahrzeug).
    vehicles: { type: Array, default: () => [] },
    activeVehicleId: { type: [Number, String, null], default: null },
  },

  // invite-email / add-mandant bleiben für bestehende Aufrufer deklariert.
  emits: ["added", "invite-email", "remove", "add-mandant"],

  data() {
    return {
      loading: false,
      contacts: [],
      caseSuggestions: [],
      targetRole: "",
      // XIX: rechts entweder die Suche ('search') oder die Beteiligten der
      // links angeklickten, besetzten Rolle ('role').
      rightView: "search",
      searchQuery: "",
      notifyBusy: false,
      targetVehicleId: null,
      caseNotificationsDisabled: false,
      mode: "search",
      newPrefill: { name: "", role: "" },
      newFormKey: 0,
      draggedHit: null,
      busyKey: "",
      isCreating: false,
      isFollowUpBusy: false,
      lastAssigned: null,
      roleChoiceList: roleChoices(),
    };
  },

  computed: {
    activeParticipants() { return this.existingParticipants.filter((p) => !p.removedAt); },
    roleGroups() { return buildRoleColumn(this.existingParticipants, this.autoInsurances.length); },
    /** Besetzte Rolle, die rechts angezeigt wird – sonst null (Suche). */
    activeRoleGroup() {
      if (this.rightView !== "role" || !this.targetRole) return null;
      const group = this.roleGroups.find((g) => g.key === this.targetRole);
      return group && !group.isEmpty ? group : null;
    },
    canOpenNotificationSettings() { return typeof this.caseDetailPage?.openNotificationSettings === "function"; },
    canEnableNotifications() { return typeof this.caseDetailPage?.enableNotifications === "function"; },
    /** Zähler der Fall-Seite: steigt nach jeder Änderung im Einstellungsdialog. */
    notificationSettingsVersion() { return this.caseDetailPage?.notificationSettingsVersion ?? 0; },
    hasMultipleVehicles() { return Array.isArray(this.vehicles) && this.vehicles.length >= 2; },
    vehicleOptions() {
      const opts = (this.vehicles || []).map((v, idx) => ({ value: Number(v.id), title: this.vehicleLabel(v, idx) }));
      return [{ value: null, title: "Alle Fahrzeuge" }, ...opts];
    },
  },

  watch: {
    notificationSettingsVersion() {
      if (this.visible) this.loadNotificationState();
    },
  },

  methods: {
    vehicleLabel(v, idx) {
      const make = [v?.manufacturer, v?.model].filter(Boolean).join(" ").trim();
      const plate = (v?.licensePlate || v?.plate || "").trim();
      const base = make || plate || `Fahrzeug ${idx + 1}`;
      return plate && make ? `${base} · ${plate}` : base;
    },

    /**
     * @param preselectedRole Rolle, die links markiert und bei „Zuordnen als …“ vorbelegt wird.
     * @param options.query   Bekannter Name (z. B. SV aus „Gutachten lt. Kunde“) als Suchbegriff.
     */
    openDialog(preselectedRole, options = {}) {
      this.visible = true;
      this.resetState();
      const known = preselectedRole === MANDANT_ROLE_KEY || (preselectedRole && normalizeRole(preselectedRole) === preselectedRole);
      this.targetRole = known ? preselectedRole : "";
      this.targetVehicleId = this.hasMultipleVehicles && this.activeVehicleId != null ? Number(this.activeVehicleId) : null;
      const query = (options.query || "").trim();
      Promise.resolve(this.loadData()).then(() => {
        if (query && this.visible) this.$nextTick(() => this.$refs.panel?.startSearch(query));
      });
      this.loadNotificationState();
    },

    closeDialog() { this.visible = false; },

    resetState() {
      this.mode = "search";
      this.rightView = "search";
      this.searchQuery = "";
      this.lastAssigned = null;
      this.draggedHit = null;
      this.busyKey = "";
      this.caseNotificationsDisabled = false;
      this.$refs.panel?.startSearch("");
    },

    async loadData() {
      this.loading = true;
      try {
        this.contacts = (await getContacts()) || [];
      } catch (e) {
        snack(`Kontakte konnten nicht geladen werden: ${e?.message || "Fehler"}`, "error");
      } finally { this.loading = false; }
      this.loadSuggestions();
    },

    /** „Aus der Akte bekannt“ – gleiche Quelle wie beim Termin (Gutachten, Fahrzeugdaten). */
    async loadSuggestions() {
      const [vehicles, workState] = await Promise.allSettled([
        this.vehicles?.length ? Promise.resolve(this.vehicles) : getRequestGeneric(`/protected/cases/${this.caseId}/vehicles`),
        getRequestGeneric(`/protected/cases/${this.caseId}/work-state`),
      ]);
      this.caseSuggestions = buildCaseSuggestions({
        vehicles: vehicles.status === "fulfilled" && Array.isArray(vehicles.value) ? vehicles.value : [],
        gutachtenSvName: workState.status === "fulfilled" ? workState.value?.gutachten?.svName : "",
      }, this.activeParticipants);
    },

    async loadNotificationState() {
      try {
        const settings = await loadCaseNotificationSettings(this.caseId);
        this.caseNotificationsDisabled = !!settings?.caseDisabled;
      } catch (e) {
        snack(`Benachrichtigungs-Einstellungen des Falls konnten nicht geladen werden: ${e?.message || "Fehler"}`, "warning");
      }
    },

    async reloadContacts() {
      try { this.contacts = (await getContacts()) || []; } catch (e) { snack(`Kontakte konnten nicht neu geladen werden: ${e?.message || "Fehler"}`, "warning"); }
    },

    /** Treffer in der gewählten Rolle zuordnen (Klick, Favoriten-Klick, Drag&Drop). */
    async assign({ hit, role, notify = true }) {
      if (this.busyKey) return null;
      this.busyKey = hit.key;
      const label = roleLabel(role);
      try {
        const result = await assignHit({ caseId: this.caseId, hit, role, vehicleId: this.targetVehicleId, notify });
        if (result.status === "exists") { snack(`${hit.name} ist bereits beteiligt.`, "warning"); return result; }
        if (result.muteError) snack(`${hit.name} wurde zugeordnet, aber die Stummschaltung konnte nicht gespeichert werden: ${result.muteError}`, "error");
        // Kundenfeedback 02.11.2026 (I): Beteiligter möchte keine KARS-Einladungen.
        if (result.invitationBlocked) snack(`${hit.name} wurde als ${label} zugeordnet – ohne KARS-Einladung, da keine Einladungen gewünscht sind.`, "info");
        else if (!hit.email && role !== MANDANT_ROLE_KEY) snack(`${hit.name} wurde ohne E-Mail-Adresse zugeordnet und kann daher nicht eingeladen werden.`, "warning");
        else snack(`${hit.name} wurde als ${label} zugeordnet.`);
        this.lastAssigned = { name: hit.name, roleLabel: label, role, hit, invitable: result.invitable, invitationBlocked: !!result.invitationBlocked, adoptable: canAdoptHit(hit) };
        this.$emit("added");
        return result;
      } catch (e) {
        snack(`${hit.name}: ${e?.message || "Zuordnung fehlgeschlagen"}`, "error");
        return null;
      } finally { this.busyKey = ""; }
    },

    /**
     * XIX: Klick links auf eine Rolle. Besetzt → rechts deren Beteiligte;
     * unbesetzt → rechts die Suche, „Zuordnen als …“ mit dieser Rolle vorbelegt.
     */
    onSelectRole(role) {
      const group = this.roleGroups.find((g) => g.key === role);
      this.mode = "search";
      this.targetRole = role;
      if (group && !group.isEmpty) {
        this.rightView = "role";
        return;
      }
      this.rightView = "search";
      this.$refs.panel?.focusInput();
    },

    /** „Weitere hinzufügen“ / „Austauschen“: Suche für diese Rolle öffnen. */
    onAddToRole(role) {
      this.mode = "search";
      this.targetRole = role;
      this.rightView = "search";
      this.$refs.panel?.startSearch("");
    },

    /** XIX: Suche links oben öffnet rechts die Suchergebnisse. */
    onLeftSearch(value) {
      if (this.rightView === "role") this.targetRole = "";
      this.rightView = "search";
      this.mode = "search";
      this.searchQuery = value;
      this.$refs.panel?.startSearch(value, { focus: false });
    },

    /** Beteiligten aus der Rollenansicht rechts zur Akte einladen. */
    async inviteMember(member) {
      const p = member?.participant;
      if (!p?.id || this.isFollowUpBusy) return;
      this.isFollowUpBusy = true;
      try {
        await inviteExternal(this.caseId, Number(p.id));
        snack(`Einladung an ${p.email} gesendet.`);
        this.$emit("added");
      } catch (e) {
        snack(`${member.name}: ${e?.message || "Einladung fehlgeschlagen"}`, "error");
      } finally { this.isFollowUpBusy = false; }
    },

    /** XVIII.1: Benachrichtigungs-Einstellungen der Akte öffnen. */
    openNotificationSettings() {
      this.caseDetailPage?.openNotificationSettings?.();
    },

    /** XVIII.1: Benachrichtigungen des Falls direkt wieder einschalten. */
    async enableCaseNotifications() {
      if (this.notifyBusy) return;
      this.notifyBusy = true;
      try {
        await this.caseDetailPage?.enableNotifications?.();
        await this.loadNotificationState();
      } finally { this.notifyBusy = false; }
    },

    onDropRole(role) {
      const hit = this.draggedHit;
      this.draggedHit = null;
      if (hit) this.assign({ hit, role, notify: true });
    },

    async toggleFavorite(hit) {
      if (!hit.contact) return;
      try {
        const updated = await toggleContactFavorite(hit.contact, hit.defaultRole);
        this.contacts = this.contacts.map((c) => (c.id === updated.id ? updated : c));
        snack(updated.isFavorite ? `${hit.name} ist jetzt Favorit (${roleLabel(normalizeRole(updated.favoriteRole))}).` : `${hit.name} ist kein Favorit mehr.`);
      } catch (e) {
        snack(`Favorit konnte nicht gespeichert werden: ${e?.message || "Fehler"}`, "error");
      }
    },

    openNewAddress({ name, role }) {
      this.newPrefill = { name: name || "", role: role || this.targetRole || "" };
      this.newFormKey += 1;
      this.lastAssigned = null;
      this.mode = "new";
    },

    /**
     * Kundenfeedback 03.11.2026 (I): Die eingegebene E-Mail-Adresse gehört
     * bereits zu einem eigenen Kontakt – diesen zuordnen statt eine Dublette
     * anzulegen.
     */
    async assignExistingContact({ contact, role }) {
      if (!contact) return;
      this.isCreating = true;
      try {
        this.mode = "search";
        await this.assign({ hit: contactToHit(contact), role, notify: true });
      } finally { this.isCreating = false; }
    },

    /** Neue Adresse als allgemeinen Kontakt anlegen, zuordnen, optional einladen. */
    async createNewAddress({ contact, role, invite }) {
      this.isCreating = true;
      try {
        const created = await createContact({ ...emptyContact(), ...contact, contactType: null });
        await this.reloadContacts();
        this.mode = "search";
        this.$refs.panel?.refresh();
        const result = await this.assign({ hit: contactToHit(created), role, notify: true });
        if (invite && result?.invitable) await this.inviteLastAssigned();
      } catch (e) {
        snack(`Adresse konnte nicht angelegt werden: ${e?.message || "Fehler"}`, "error");
      } finally { this.isCreating = false; }
    },

    async inviteLastAssigned() {
      const target = this.lastAssigned?.invitable;
      if (!target) return;
      this.isFollowUpBusy = true;
      try {
        await inviteExternal(this.caseId, target.id);
        snack(`Einladung an ${target.email} gesendet.`);
        this.lastAssigned = { ...this.lastAssigned, invitable: null };
        this.$emit("added");
      } catch (e) {
        snack(`${target.name}: ${e?.message || "Einladung fehlgeschlagen"}`, "error");
      } finally { this.isFollowUpBusy = false; }
    },

    async adoptLastAssigned(asFavorite) {
      const entry = this.lastAssigned;
      if (!entry?.adoptable) return;
      this.isFollowUpBusy = true;
      try {
        await adoptHitAsContact(entry.hit, entry.role, asFavorite);
        await this.reloadContacts();
        this.$refs.panel?.refresh();
        snack(asFavorite ? `${entry.name} ist jetzt in Ihren Favoriten (${entry.roleLabel}).` : `${entry.name} wurde in Ihre Kontakte übernommen.`);
        this.lastAssigned = { ...entry, adoptable: false };
      } catch (e) {
        snack(`${entry.name}: ${e?.message || "Übernahme fehlgeschlagen"}`, "error");
      } finally { this.isFollowUpBusy = false; }
    },

    openContacts() {
      window.open(this.$router.resolve({ name: "PageContacts" }).href, "_blank", "noopener");
    },
  },
};
</script>

<style scoped>
.apd {
  display: flex; flex-direction: column; max-height: 90vh; overflow: hidden;
  border-radius: 16px; background: #fff; color: var(--dark-color);
  box-shadow: 0 24px 80px rgba(0, 0, 0, .16);
}
.apd__head {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  padding: 18px 22px 14px; background: #fff; border-bottom: 1px solid var(--border-grey-color-light);
}
.apd__title { margin: 0; font-size: 17px; font-weight: 700; }
.apd__sub { margin: 2px 0 0; font-size: 12.5px; color: var(--grey-color); }
.apd__close { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border: none; border-radius: 8px; background: none; color: var(--grey-color); cursor: pointer; }
.apd__close:hover { background: var(--grey-light-color); color: var(--dark-color); }
.apd__loading { display: flex; align-items: center; justify-content: center; padding: 64px 0; background: #fff; }

.apd__split { display: flex; flex: 1; min-height: 0; overflow: hidden; }
.apd__left { width: 290px; flex-shrink: 0; padding: 16px 12px; overflow-y: auto; border-right: 1px solid var(--border-grey-color-light); background: #fff; }
.apd__right { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 12px; padding: 16px 20px; overflow-y: auto; }
.apd__note { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 12.5px; color: var(--dark-color); }
.apd__note-ico { flex-shrink: 0; color: var(--grey-color); }
.apd__note-text { margin-right: 4px; }
.apd__note-btn {
  height: 28px; padding: 0 10px; border: 1px solid var(--border-grey-color); border-radius: 8px; background: #fff;
  font: inherit; font-size: 12px; font-weight: 600; color: var(--primary-color); cursor: pointer; white-space: nowrap;
}
.apd__note-btn:hover:not(:disabled) { border-color: var(--primary-color); }
.apd__note-btn:disabled { opacity: .5; cursor: not-allowed; }
.apd__note-btn:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.apd__vehicle { display: flex; align-items: center; gap: 12px; }
.apd__vehicle-label { font-size: 12.5px; font-weight: 600; white-space: nowrap; }
.apd__vehicle-select { max-width: 280px; }

.apd__foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 20px; background: #fff; border-top: 1px solid var(--border-grey-color-light); }
.apd__foot-hint { font-size: 12px; color: var(--grey-color); }
.apd__btn-close { height: 36px; padding: 0 18px; border: none; border-radius: 10px; background: var(--dark-color); color: #fff; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.apd__btn-close:hover { background: #000; }
.apd__btn-close:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }

.apd__left::-webkit-scrollbar, .apd__right::-webkit-scrollbar { width: 5px; }
.apd__left::-webkit-scrollbar-thumb, .apd__right::-webkit-scrollbar-thumb { background: var(--border-grey-color); border-radius: 3px; }

@media (max-width: 760px) {
  .apd__split { flex-direction: column; overflow-y: auto; }
  .apd__left { width: 100%; max-height: 34vh; border-right: none; border-bottom: 1px solid var(--border-grey-color-light); }
  .apd__foot-hint { display: none; }
}
</style>
